import type { PageData } from "./context";
import type { PlatformView } from "./model";

export interface HostSummary {
  /** Cloud hosts with a connection the backend would actually use (connected or expiring). */
  usableHosts: PlatformView[];
  /** Other platforms (blogs, socials...) with a usable connection. */
  usableOthers: PlatformView[];
  /** Platforms that have a connection which cannot be used right now (reconnect needed, disconnected...). */
  needsAttention: { platform: string; status: string }[];
  /** Cloud hosts with no connection at all. */
  unconnectedHosts: number;
  /** Every registered platform that has no usable connection. */
  availableToConnect: number;
}

/**
 * The old page counted any connection row as "connected", so a host needing a reconnect still got a Deploy button that
 * the backend then refused. A connection counts only while its status is one the publishing service resolves.
 */
export function summariseHosts(data: Pick<PageData, "connections" | "platforms">): HostSummary {
  const usable = new Set(data.connections.filter((c) => c.usable).map((c) => c.platform));
  const anyConnection = new Map<string, string>();
  for (const c of data.connections) if (!anyConnection.has(c.platform) || c.usable) anyConnection.set(c.platform, c.status);
  const hosts = data.platforms.filter((p) => p.category === "cloud_host");
  const usableHosts = hosts.filter((p) => usable.has(p.platform));
  const usableOthers = data.platforms.filter((p) => p.category !== "cloud_host" && usable.has(p.platform));
  const needsAttention = data.platforms.filter((p) => !usable.has(p.platform) && anyConnection.has(p.platform)).map((p) => ({ platform: p.platform, status: anyConnection.get(p.platform) as string }));
  return {
    usableHosts,
    usableOthers,
    needsAttention,
    unconnectedHosts: hosts.filter((p) => !anyConnection.has(p.platform)).length,
    availableToConnect: data.platforms.filter((p) => !usable.has(p.platform)).length,
  };
}
